/**
 * yt-bundle-pdp.js — controller for sections/yt-bundle-pdp.liquid (the
 * bundle-world clone of the formula PDP).
 *
 * There is no minted variant: the blend arrives in sessionStorage under
 * `yt_blend_summary`, written by yt-byo.js at submit:
 *   { name, mode: 'sub'|'onetime', prices: {sub, one_time}, fee,
 *     ingredients: [{name, slug, dose_num, dose_unit}],
 *     bundle: { items: [{variant_id, quantity, selling_plan_30d, …}],
 *               properties: {...} },
 *     spoon_variant_id, formula_id, at }
 *
 * This controller hydrates every dynamic node the legacy section fills
 * from metafields, then runs the same interactions: offer selection,
 * sticky bar, breakdown, and the two CTAs — which add the COMPONENT
 * lines (plus the free weighing spoon, deduped to one per cart) and go
 * to /cart or /checkout. Cold visits (no stored blend) bounce to the
 * builder, preserving the parent PDP's old redirect behaviour.
 */
(function () {
  'use strict';

  /* Tin-name cap — see formulator/src/formula/name.ts and migration 168. */
  var TIN_NAME_MAX = 22;
  var STORAGE_KEY = 'yt_blend_summary';
  var BUILDER_URL = '/pages/build-your-own';

  function money(n) {
    return '£' + (Math.round(Number(n) * 100) / 100).toFixed(2);
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function validPayload(p) {
    return p && p.bundle && Array.isArray(p.bundle.items) && p.bundle.items.length > 0;
  }

  async function boot() {
    var root = document.querySelector('[data-yt-bundle-pdp]');
    if (!root) return;

    var payload = null;
    try { payload = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null'); } catch (_e) {}

    // SHAREABLE LINKS: ?blend=<formula id> is the page's real identity.
    // sessionStorage is only the fast path for the customer who just
    // submitted — anyone else (or any other device) hydrates from the
    // formulator via the App Proxy. A fetched payload is cached back so
    // refreshes are instant.
    var urlId = new URLSearchParams(window.location.search).get('blend');
    // Refetch when the stored payload predates 90-day commissioning (no
    // items_90d) — otherwise a stale fast-path hides the default offer.
    var storageStale = validPayload(payload) &&
      (!(payload.bundle.items_90d && payload.bundle.items_90d.length) || !payload.nutrition);
    if (urlId && /^[0-9a-f-]{36}$/i.test(urlId) && (!validPayload(payload) || payload.formula_id !== urlId || storageStale)) {
      try {
        // 2026-09-02: the section kicks this fetch off inline at parse time
        // (window.__ytPdpPrefetch) so it isn't gated on the deferred bundle.
        var pre = window.__ytPdpPrefetch;
        var res = (pre && pre.id === urlId && pre.p)
          ? await pre.p
          : await fetch('/apps/yutrients/formula/' + encodeURIComponent(urlId) + '/pdp', {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
        window.__ytPdpPrefetch = null;
        if (res.ok) {
          var fetched = await res.json();
          if (validPayload(fetched)) {
            payload = fetched;
            try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fetched)); } catch (_e) {}
          }
        }
      } catch (_e) { /* fall through to storage / redirect */ }
    }

    if (!validPayload(payload)) {
      // No blend in hand — this page has nothing to show; go build one.
      window.location.replace(BUILDER_URL);
      return;
    }
    // Make THIS url shareable even when we arrived via sessionStorage.
    if (payload.formula_id && urlId !== payload.formula_id) {
      try {
        var u = new URL(window.location.href);
        u.searchParams.set('blend', payload.formula_id);
        window.history.replaceState({}, '', u.toString());
      } catch (_e) {}
    }

    // The engraving/rename overlay that used to live here is removed: no
    // customer names a tin any more (founder, 28 Sep 2026). The server
    // refuses customer renames (formulator src/account/engrave.ts).

    var name = payload.name || 'My Formula';
    var prices = payload.prices || {};
    var subPrice = Number(prices.sub) || 0;
    var oneTimePrice = Number(prices.one_time) || Math.round(subPrice * 1.25 * 100) / 100;
    var fee = payload.fee == null ? 15 : Number(payload.fee); // 0 is REAL (base blends have no fee line)
    var baseBlock = payload.base || null;
    var ings = Array.isArray(payload.ingredients) ? payload.ingredients : [];
    var editUrl = BUILDER_URL + (payload.formula_id ? '?stack=' + encodeURIComponent(payload.formula_id) : '');

    // ── static text hydration ─────────────────────────────────────────
    document.title = name + ' — Yutrients';
    root.querySelectorAll('.yt-formula-pdp__tin-overlay-text--dynamic').forEach(function (el) {
      el.textContent = name;
    });
    root.querySelectorAll('[data-yt-bundle-ingcount]').forEach(function (el) {
      el.textContent = String(ings.length);
    });
    // Any heading that renders the blend name (title node classes vary by
    // theme revision — cover the known ones).
    root.querySelectorAll('.yt-formula-pdp__title, [data-yt-formula-title]').forEach(function (el) {
      el.textContent = name;
    });
    // The H1 is "<name> <suffix span>" — replace only the leading text
    // node so the '- Your Made-to-Order…' suffix keeps its own styling.
    var h1 = root.querySelector('.yt-formula-pdp__desc-title');
    if (h1 && h1.firstChild && h1.firstChild.nodeType === 3) {
      h1.firstChild.textContent = name + ' ';
    }
    root.querySelectorAll('.yt-formula-pdp__edit-pill, .yt-formula-pdp__add-more, .yt-formula-pdp__cta-helper').forEach(function (a) {
      a.setAttribute('href', editUrl);
    });

    // Founder 2026-08-28: a CUSTOM-named base blend titles as
    // "<Name> - <the base landing page's headline>" ("Jeff - Fall asleep
    // faster. Wake up properly rested."). Default-named blends (base name
    // or My formula) keep the standard title. The headline's source of
    // truth is the landing page itself (theme-editor editable), so we read
    // its server-rendered H1 rather than duplicating the copy anywhere.
    function applyBaseTitle(currentName) {
      if (!baseBlock || !baseBlock.slug || !currentName) return;
      var trimmed = String(currentName).trim();
      var isDefault =
        /^my formula$/i.test(trimmed) ||
        trimmed.toLowerCase() === String(baseBlock.name || '').trim().toLowerCase();
      if (isDefault) return;
      fetch('/products/base-' + encodeURIComponent(baseBlock.slug), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var srcEl = doc.querySelector('.yt-formula-pdp__desc-title');
          var headline = srcEl ? srcEl.textContent.trim() : '';
          if (!headline) return;
          var h1c = root.querySelector('.yt-formula-pdp__desc-title');
          if (h1c) {
            h1c.textContent = '';
            h1c.appendChild(document.createTextNode(trimmed + ' '));
            var span = document.createElement('span');
            span.className = 'yt-formula-pdp__desc-title-suffix';
            span.textContent = '- ' + headline;
            h1c.appendChild(span);
          }
          document.title = trimmed + ' - ' + headline + ' — Yutrients';
        })
        .catch(function () { /* title nicety — never block the page */ });
    }
    applyBaseTitle(name);

    // Stale links still carrying ?engrave=1 (the retired naming overlay)
    // just get the param tidied off the URL.
    try {
      var eu = new URL(window.location.href);
      if (eu.searchParams.has('engrave')) {
        eu.searchParams.delete('engrave');
        window.history.replaceState({}, '', eu.toString());
      }
    } catch (_e) {}

    // ── offer cards ───────────────────────────────────────────────────
    var perDaySub = subPrice / 30;
    var has90 = !!(payload.bundle.items_90d && payload.bundle.items_90d.length && payload.bundle.bundle_key_90d &&
      payload.bundle.items_90d.every(function (i) { return i.variant_id; }) &&
      payload.bundle.items_90d.some(function (i) { return i.selling_plan_90d; }));
    var monthly90 = Math.round(oneTimePrice * 0.7 * 100) / 100;
    var quarter90 = Math.round(monthly90 * 3 * 100) / 100;
    var card3mo = root.querySelector('[data-yt-plan="3mo"]');
    var sticky3moOpt = root.querySelector('[data-yt-sticky-option="3mo"]');
    if (!has90) {
      if (card3mo) card3mo.style.display = 'none';
      if (sticky3moOpt) sticky3moOpt.style.display = 'none';
    } else if (card3mo) {
      var p3 = card3mo.querySelector('.yt-formula-pdp__offer-price');
      if (p3) p3.innerHTML = esc(money(monthly90)) + '<span class="yt-formula-pdp__offer-price-unit">/mo</span>';
      var serving3 = card3mo.querySelector('.yt-formula-pdp__offer-serving');
      if (serving3) serving3.textContent = money(monthly90 / 30) + ' GBP / SERVING';
      var meta3 = card3mo.querySelector('.yt-formula-pdp__offer-meta');
      if (meta3) meta3.innerHTML = '<span>' + esc(money(quarter90)) + ' GBP every 90 days &middot; delivered monthly</span>';
    }
    var card1mo = root.querySelector('[data-yt-plan="1mo"]');
    if (card1mo) {
      var p = card1mo.querySelector('.yt-formula-pdp__offer-price');
      if (p) p.innerHTML = esc(money(subPrice)) + '<span class="yt-formula-pdp__offer-price-unit">/mo</span>';
      var meta = card1mo.querySelector('.yt-formula-pdp__offer-meta');
      if (meta) meta.innerHTML =
        '<span>' + esc(money(subPrice)) + ' GBP every 30 days</span>' +
        '<span class="yt-formula-pdp__offer-serving">' + esc(money(perDaySub)) + ' GBP / SERVING</span>';
    }
    var cardOne = root.querySelector('[data-yt-plan="onetime"]');
    if (cardOne) {
      var link = cardOne.querySelector('[data-yt-onetime-link]');
      if (link) link.textContent = 'One Time Purchase ' + money(oneTimePrice);
      var op = cardOne.querySelector('.yt-formula-pdp__offer-price--onetime');
      if (op) op.textContent = money(oneTimePrice);
    }

    // ── YOUR BLEND rows ───────────────────────────────────────────────
    var list = root.querySelector('.yt-formula-pdp__stack-list');
    if (list) {
      ings.forEach(function (ing) {
        if (ing.dose_num == null && ing.amount_mg != null) {
          // bn_cfu probiotics: the number is billions of CFU (checked before
          // the sub-1mg µg fallback, which would turn 0.5 into "500µg").
          var isCfu = String(ing.unit || '').toLowerCase() === 'bn_cfu';
          var isMcg = !isCfu && (ing.unit === 'mcg' || (ing.unit == null && ing.amount_mg > 0 && ing.amount_mg < 1));
          ing.dose_num = String(isMcg ? Math.round(ing.amount_mg * 1000) : ing.amount_mg);
          ing.dose_unit = isCfu ? ' bn CFU' : isMcg ? 'µg' : 'mg';
        }
      });
      list.innerHTML = ings.map(function (ing) {
        return (
          '<li class="yt-formula-pdp__stack-row">' +
            '<div class="yt-formula-pdp__stack-thumb" aria-hidden="true" data-yt-ing-slug="' + esc(ing.slug || '') + '" data-yt-ing-name="' + esc(ing.name) + '">' +
              '<span class="yt-formula-pdp__stack-thumb-img"></span>' +
            '</div>' +
            '<div class="yt-formula-pdp__stack-body">' +
              '<span class="yt-formula-pdp__stack-name">' + esc(ing.name) + '</span>' +
            '</div>' +
            '<span class="yt-formula-pdp__stack-figures">' +
              '<span class="yt-formula-pdp__stack-price" data-yt-share="' + esc(ing.share || 0) + '"></span>' +
              '<span class="yt-formula-pdp__stack-dose">' + esc(ing.dose_num) +
                '<span class="yt-formula-pdp__stack-unit">' + esc(ing.dose_unit) + ' /day</span>' +
              '</span>' +
            '</span>' +
          '</li>'
        );
      }).join('');
      // B5: base as the FIRST row — inserted AFTER innerHTML so it survives
      // the rows render (2026-08-27 regression: it was wiped before).
      if (baseBlock) {
      // B5: the base is the TOP ROW of the blend list — one locked line at
      // its own price (per-selection basis), linking to its PDP; the add-on
      // rows with their £ shares follow.
      var baseRow = document.createElement('li');
      baseRow.className = 'yt-formula-pdp__stack-row yt-formula-pdp__stack-row--base';
      baseRow.style.borderBottom = '2px solid #1c1c1c';
      // Same column anatomy as the add-on rows (price + dose slots) so the
      // base price LINES UP with the other figures (founder 2026-08-27).
      baseRow.innerHTML =
        '<div class="yt-formula-pdp__stack-thumb" aria-hidden="true"><span class="yt-formula-pdp__stack-thumb-img" style="display:inline-flex;width:100%;height:100%;align-items:center;justify-content:center;background:var(--yt-tint-blue,#e9f6ff);border-radius:10px;font-family:var(--yt-font-heading,Oswald,sans-serif);font-size:13px;">BASE</span></div>' +
        '<div class="yt-formula-pdp__stack-body">' +
          '<a href="/products/base-' + esc(baseBlock.slug) + '" style="text-decoration:none;color:inherit;">' +
            '<span class="yt-formula-pdp__stack-name" style="font-weight:700;">' + esc(baseBlock.name) + '</span>' +
          '</a>' +
          '<span style="display:block;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:#555;">Base &middot; ' + esc(baseBlock.n_ingredients) + ' ingredients</span>' +
        '</div>' +
        '<span class="yt-formula-pdp__stack-figures">' +
          '<span class="yt-formula-pdp__stack-price" data-yt-base-price></span>' +
          '<span class="yt-formula-pdp__stack-dose">' + esc(baseBlock.n_ingredients) +
            '<span class="yt-formula-pdp__stack-unit">ingredients</span>' +
          '</span>' +
        '</span>';
      if (list.firstChild) list.insertBefore(baseRow, list.firstChild);
      else list.appendChild(baseRow);
    }
      // Ingredient photos — same technique as the legacy PDP: every
      // catalogue ingredient is a product whose handle equals the slug.
      list.querySelectorAll('[data-yt-ing-slug]').forEach(function (thumb) {
        var slug = thumb.getAttribute('data-yt-ing-slug');
        if (!slug) return;
        var baked = (window.__ytIngredientImages || {})[slug];
        if (baked) {
          var bs = thumb.querySelector('.yt-formula-pdp__stack-thumb-img');
          if (bs) bs.style.backgroundImage = 'url(' + baked + ')';
          return;
        }
        fetch('/products/' + encodeURIComponent(slug) + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (prod) {
            var img = prod && (prod.featured_image || (prod.images && prod.images[0]));
            if (!img) return;
            var span = thumb.querySelector('.yt-formula-pdp__stack-thumb-img');
            if (span) span.style.backgroundImage = 'url(' + img + ')';
          })
          .catch(function () {});
      });
    }

    // ── EDITABLE BLEND (founder 2026-08-28): the fee-carrying custom
    // blend gets the base-landing treatment — dose steppers + remove on
    // every row, an upsell carousel, live re-pricing, and ADD TO CART
    // forks a fresh formula when anything changed (always-fresh rule,
    // same name). Base blends are untouched here — they live on their
    // landing pages now. `ensureBlendCurrent` is hoisted (var) so
    // addAndGo can always await it.
    var ensureBlendCurrent = async function () {};
    var blendDirty = false;
    if (!baseBlock && list && payload.formula_id && ings.length) {
      (function () {
        // Lucas, 29 Sep 2026: L-theanine and FISH-OIL omega-3 are not
        // recommended anywhere any more. The algal omega-3 takes the
        // omega-3 slot; both remain buildable in the builder.
        var UPSELL_DEFAULTS = [
          'creatine', 'magnesium-glycinate', 'omega-3-total-omega-3-fatty-acids-11-6-dha-3-epa',
          'vitamin-d3', 'ashwagandha', 'collagen-type-i-bovine-hydrolysed',
          'coq10', 'turmeric-curcumin', 'zinc', 'vitamin-b12-2', 'lion-s-mane',
          'vitamin-c-ascorbic-acid', 'hyaluronic-acid', 'vitamin-b7-biotin',
        ];
        var catRows = null, catBySlug = null, catById = null, P = null;
        var lines = null; // [{ingredient_id, amount_mg}] — the editable truth
        var photoCache = {};

        function doseDisp(mg, unit) {
          if (String(unit || '').toLowerCase() === 'bn_cfu') return (Math.round(mg * 100) / 100) + ' bn CFU';
          if (unit === 'mcg' || (mg > 0 && mg < 1)) return Math.round(mg * 1000) + 'µg';
          var v = Math.round(mg * 100) / 100;
          if (v >= 1000 && v % 100 === 0) return (v / 1000) + 'g';
          return (Number.isInteger(v) ? v : v.toFixed(2)) + 'mg';
        }
        function round2(n) { return Math.round(n * 100) / 100; }
        function computeOffers() {
          var total = P.base;
          lines.forEach(function (l) {
            var c = catById[l.ingredient_id];
            if (!c || c.raw_cost_per_mg == null) return;
            total += Number(c.price_fee || 0) + Number(c.price_multiple || 0) * Number(c.raw_cost_per_mg) * l.amount_mg;
          });
          total -= P.taperGbp * Math.max(0, lines.length - P.taperAfter);
          var sub = round2(Math.max(P.floor, total));
          var ot = round2(sub * P.oneTimeMult);
          var m90 = Math.max(round2(ot * P.bundleMult), P.prepaidFloor);
          return { sub: sub, one_time: ot, m90: m90 };
        }
        function applyPrices() {
          var pr = computeOffers();
          subPrice = pr.sub; oneTimePrice = pr.one_time;
          monthly90 = pr.m90; quarter90 = round2(pr.m90 * 3); perDaySub = pr.sub / 30;
          var c3 = root.querySelector('[data-yt-plan="3mo"]');
          if (c3) {
            var p3b = c3.querySelector('.yt-formula-pdp__offer-price');
            if (p3b) p3b.innerHTML = esc(money(monthly90)) + '<span class="yt-formula-pdp__offer-price-unit">/mo</span>';
            var s3 = c3.querySelector('.yt-formula-pdp__offer-serving');
            if (s3) s3.textContent = money(monthly90 / 30) + ' GBP / SERVING';
            var m3 = c3.querySelector('.yt-formula-pdp__offer-meta');
            if (m3) m3.innerHTML = '<span>' + esc(money(quarter90)) + ' GBP every 90 days &middot; delivered monthly</span>';
          }
          var c1 = root.querySelector('[data-yt-plan="1mo"]');
          if (c1) {
            var p1b = c1.querySelector('.yt-formula-pdp__offer-price');
            if (p1b) p1b.innerHTML = esc(money(subPrice)) + '<span class="yt-formula-pdp__offer-price-unit">/mo</span>';
            var m1 = c1.querySelector('.yt-formula-pdp__offer-meta');
            if (m1) m1.innerHTML = '<span>' + esc(money(subPrice)) + ' GBP every 30 days</span>' +
              '<span class="yt-formula-pdp__offer-serving">' + esc(money(perDaySub)) + ' GBP / SERVING</span>';
          }
          var cOne = root.querySelector('[data-yt-plan="onetime"]');
          if (cOne) {
            var lk = cOne.querySelector('[data-yt-onetime-link]');
            if (lk) lk.textContent = 'One Time Purchase ' + money(oneTimePrice);
            var op2 = cOne.querySelector('.yt-formula-pdp__offer-price--onetime');
            if (op2) op2.textContent = money(oneTimePrice);
          }
          paintLinePrices();
          renderSelection();
        }
        // Founder 2026-09-03 ("lots of reasonable products, not one big
        // expensive one"): every row shows ITS OWN price at the selected
        // plan, computed from the catalogue — on first paint and after every
        // edit, never hidden. The closing totals row rides with it.
        function paintLinePrices() {
          if (!lines || !catById) return;
          var lineBasis = selected === 'onetime' ? P.oneTimeMult : selected === '3mo' ? P.oneTimeMult * P.bundleMult : 1;
          list.querySelectorAll('[data-yt-blend-row]').forEach(function (row) {
            var c = catById[row.getAttribute('data-yt-blend-row')];
            var l = lines.find(function (x) { return x.ingredient_id === row.getAttribute('data-yt-blend-row'); });
            var pe = row.querySelector('[data-yt-line-price]');
            if (!c || !l || !pe || c.raw_cost_per_mg == null) return;
            var lp = round2((Number(c.price_fee || 0) + Number(c.price_multiple || 0) * Number(c.raw_cost_per_mg) * l.amount_mg) * lineBasis);
            pe.textContent = money(lp) + (selected === 'onetime' ? '' : '/mo');
          });
          renderStackTotal(lines.length, selected === 'onetime' ? oneTimePrice : selected === '3mo' ? monthly90 : subPrice, selected === 'onetime');
        }
        function setPhoto(rowThumb, slug) {
          var span = rowThumb.querySelector('.yt-formula-pdp__stack-thumb-img');
          if (!span) return;
          if (!photoCache[slug] && (window.__ytIngredientImages || {})[slug]) photoCache[slug] = window.__ytIngredientImages[slug];
          if (photoCache[slug]) { span.style.backgroundImage = 'url(' + photoCache[slug] + ')'; return; }
          fetch('/products/' + encodeURIComponent(slug) + '.js')
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (prod) {
              var img = prod && (prod.featured_image || (prod.images && prod.images[0]));
              if (!img) return;
              photoCache[slug] = img;
              span.style.backgroundImage = 'url(' + img + ')';
            })
            .catch(function () {});
        }
        function renderEditableRows() {
          list.innerHTML = lines.map(function (l) {
            var c = catById[l.ingredient_id];
            if (!c) return '';
            var min = Number(c.min_dose_mg) || Number(c.dose_step_mg) || 1;
            var max = Number(c.max_dose_mg) || min;
            return (
              '<li class="yt-formula-pdp__stack-row" data-yt-blend-row="' + esc(c.id) + '">' +
                '<div class="yt-formula-pdp__stack-thumb" aria-hidden="true" data-yt-ing-slug="' + esc(c.slug) + '">' +
                  '<span class="yt-formula-pdp__stack-thumb-img"></span>' +
                '</div>' +
                '<div class="yt-formula-pdp__stack-body">' +
                  '<span class="yt-formula-pdp__stack-name">' + esc(c.name) + '</span>' +
                  '<span class="yt-formula-pdp__stack-lineprice" data-yt-line-price></span>' +
                '</div>' +
                '<span class="yt-formula-pdp__stack-figures" style="display:inline-flex;align-items:center;gap:8px;">' +
                  '<span class="yt-base-landing__step">' +
                    '<button type="button" class="yt-base-landing__step-btn" data-yt-step="-1" aria-label="Decrease dose"' + (l.amount_mg <= min ? ' disabled' : '') + '>&minus;</button>' +
                    '<span class="yt-base-landing__step-val">' + esc(doseDisp(l.amount_mg, c.unit)) + '</span>' +
                    '<button type="button" class="yt-base-landing__step-btn" data-yt-step="1" aria-label="Increase dose"' + (l.amount_mg >= max ? ' disabled' : '') + '>+</button>' +
                  '</span>' +
                  '<button type="button" class="yt-base-landing__row-remove" data-yt-blend-remove aria-label="Remove ' + esc(c.name) + '">&times;</button>' +
                '</span>' +
              '</li>'
            );
          }).join('');
          list.querySelectorAll('[data-yt-blend-row]').forEach(function (row) {
            var id = row.getAttribute('data-yt-blend-row');
            var line = lines.find(function (l) { return l.ingredient_id === id; });
            var c = catById[id];
            if (!line || !c) return;
            setPhoto(row.querySelector('[data-yt-ing-slug]'), c.slug);
            row.querySelectorAll('[data-yt-step]').forEach(function (btn) {
              btn.addEventListener('click', function () {
                var step = Number(c.dose_step_mg) || 1;
                var min = Number(c.min_dose_mg) || step;
                var max = Number(c.max_dose_mg) || min;
                var next = line.amount_mg + step * Number(btn.getAttribute('data-yt-step'));
                if (next < min) next = min;
                if (next > max) next = max;
                if (next === line.amount_mg) return;
                line.amount_mg = next;
                blendDirty = true;
                refresh();
              });
            });
            var rm = row.querySelector('[data-yt-blend-remove]');
            if (rm) rm.addEventListener('click', function () {
              if (lines.length <= 1) return; // a blend keeps at least one line
              lines = lines.filter(function (l) { return l.ingredient_id !== id; });
              blendDirty = true;
              refresh();
            });
          });
        }
        var carouselEl = null;
        function renderUpsellCards() {
          if (!carouselEl) return;
          var grid = carouselEl.querySelector('[data-yt-blend-upsell-grid]');
          var present = {};
          lines.forEach(function (l) { var c = catById[l.ingredient_id]; if (c) present[c.slug] = true; });
          var picks = [];
          UPSELL_DEFAULTS.forEach(function (s) {
            var c = catBySlug[s];
            if (!c || present[s] || c.raw_cost_per_mg == null) return;
            if (picks.length < 10) picks.push(c);
          });
          grid.innerHTML = picks.map(function (c) {
            var priceSub = Number(c.price_fee || 0) + Number(c.price_multiple || 0) * Number(c.raw_cost_per_mg) * Number(c.default_dose_mg);
            var basis = selected === 'onetime' ? P.oneTimeMult : selected === '3mo' ? 0.875 : 1;
            return '<div class="yt-base-landing__card" data-yt-blend-card="' + esc(c.id) + '">' +
              '<img class="yt-base-landing__card-img" alt="' + esc(c.name) + '" loading="lazy">' +
              '<span class="yt-base-landing__card-top"><span class="yt-base-landing__card-price">+' + esc(money(round2(priceSub * basis))) + (selected === 'onetime' ? '' : '/mo') + '</span></span>' +
              '<div class="yt-base-landing__card-body">' +
                '<span class="yt-base-landing__card-name">' + esc(c.name) + '</span>' +
                '<span class="yt-base-landing__card-dose">' + esc(doseDisp(Number(c.default_dose_mg), c.unit)) + ' daily</span>' +
                '<button type="button" class="yt-base-landing__card-add">ADD</button>' +
              '</div>' +
            '</div>';
          }).join('');
          grid.querySelectorAll('[data-yt-blend-card]').forEach(function (card) {
            var id = card.getAttribute('data-yt-blend-card');
            var c = catById[id];
            if (!c) return;
            var img = card.querySelector('img');
            if (!photoCache[c.slug] && (window.__ytIngredientImages || {})[c.slug]) photoCache[c.slug] = window.__ytIngredientImages[c.slug];
            if (photoCache[c.slug]) img.src = photoCache[c.slug];
            else {
              fetch('/products/' + encodeURIComponent(c.slug) + '.js')
                .then(function (r) { return r.ok ? r.json() : null; })
                .then(function (prod) {
                  var im = prod && (prod.featured_image || (prod.images && prod.images[0]));
                  if (im) { photoCache[c.slug] = im; img.src = im; }
                })
                .catch(function () {});
            }
            card.querySelector('.yt-base-landing__card-add').addEventListener('click', function () {
              lines.push({ ingredient_id: c.id, amount_mg: Number(c.default_dose_mg) });
              blendDirty = true;
              if (window.ytTrack) { try { ytTrack('blend_pdp_upsell_added', { ingredient: c.name }); } catch (_e) {} }
              refresh();
            });
          });
        }
        function refresh() {
          renderEditableRows();
          renderUpsellCards();
          applyPrices();
        }

        ensureBlendCurrent = async function () {
          if (!blendDirty || !lines) return;
          var res = await fetch('/apps/yutrients/formula/submit', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Idempotency-Key': (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now()) + Math.random(),
            },
            body: JSON.stringify({
              stack: {
                stack_name: (payload.name || 'My formula').slice(0, TIN_NAME_MAX),
                primary_goals: [],
                ingredients: lines.map(function (l) { return { ingredient_id: l.ingredient_id, amount_mg: l.amount_mg }; }),
              },
            }),
          });
          if (!res.ok) {
            var detail = 'Something went wrong updating your blend — please try again.';
            try {
              var err = await res.json();
              if (err && err.error) detail = (err.error.details && err.error.details.message_for_human) || err.error.message || detail;
            } catch (_e) {}
            throw new Error(detail);
          }
          var out = await res.json();
          if (!out.formula_id) throw new Error('Something went wrong updating your blend — please try again.');
          var pdpRes = await fetch('/apps/yutrients/formula/' + encodeURIComponent(out.formula_id) + '/pdp', {
            credentials: 'same-origin', headers: { Accept: 'application/json' },
          });
          if (!pdpRes.ok) throw new Error('Something went wrong preparing your order — please try again.');
          var fresh = await pdpRes.json();
          if (!fresh.bundle || !fresh.bundle.items || !fresh.bundle.items.length) {
            throw new Error('Something went wrong preparing your order — please try again.');
          }
          payload = fresh;
          has90 = !!(payload.bundle.items_90d && payload.bundle.items_90d.length && payload.bundle.bundle_key_90d &&
            payload.bundle.items_90d.every(function (i) { return i.variant_id; }) &&
            payload.bundle.items_90d.some(function (i) { return i.selling_plan_90d; }));
          blendDirty = false;
          try {
            var u = new URL(window.location.href);
            u.searchParams.set('blend', out.formula_id);
            window.history.replaceState({}, '', u.toString());
          } catch (_e) {}
        };

        fetch('/apps/yutrients/catalogue', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (cat) {
            if (!cat || !Array.isArray(cat.ingredients)) return;
            catRows = cat.ingredients;
            catBySlug = {}; catById = {};
            catRows.forEach(function (c) { catBySlug[c.slug] = c; catById[c.id] = c; });
            var pr = (cat.pricing || {});
            P = {
              floor: Number.isFinite(Number(pr.variant_floor)) ? Number(pr.variant_floor) : 50,
              oneTimeMult: Number(pr.one_time_multiplier) > 0 ? Number(pr.one_time_multiplier) : 1.25,
              bundleMult: Number(pr.bundle_multiplier) > 0 ? Number(pr.bundle_multiplier) : 0.7,
              prepaidFloor: Number.isFinite(Number(pr.prepaid_floor)) ? Number(pr.prepaid_floor) : 45,
              base: Number.isFinite(Number(pr.base)) ? Number(pr.base) : 11 / 0.37,
              taperAfter: Number.isFinite(Number(pr.taper_after)) ? Number(pr.taper_after) : 15,
              taperGbp: Number.isFinite(Number(pr.taper_gbp)) ? Number(pr.taper_gbp) : 0,
            };
            // Every line must map to a live catalogue row (by slug) or we
            // can't rebuild the formula faithfully — leave read-only then.
            var mapped = [];
            for (var i = 0; i < ings.length; i += 1) {
              var c = catBySlug[ings[i].slug];
              // /pdp serves DISPLAY doses (dose_num in the shown unit) —
              // canonical mg = ÷1000 for µg rows.
              var rawDose = Number(ings[i].amount_mg != null ? ings[i].amount_mg : ings[i].dose_num);
              var mg = ings[i].dose_unit === 'µg' ? rawDose / 1000 : rawDose;
              if (!c || !isFinite(mg) || mg <= 0) return;
              mapped.push({ ingredient_id: c.id, amount_mg: mg });
            }
            lines = mapped;
            // Upsell carousel after the blend list (same design as the
            // base landing pages).
            carouselEl = document.createElement('div');
            carouselEl.className = 'yt-base-landing__upsells';
            carouselEl.innerHTML =
              '<h2 class="yt-formula-pdp__caps yt-base-landing__upsell-heading">MAKE IT YOURS — POPULAR ADD-ONS</h2>' +
              '<p class="yt-base-landing__upsell-sub">Blended into the same scoop — no extra tubs, no extra tins.</p>' +
              '<div class="yt-base-landing__upsell-carousel">' +
                '<button type="button" class="yt-base-landing__upsell-arrow yt-base-landing__upsell-arrow--prev" data-yt-blend-upsell-prev aria-label="Previous add-ons">&#8249;</button>' +
                '<div class="yt-base-landing__upsell-grid" data-yt-blend-upsell-grid tabindex="0" role="group" aria-label="Popular add-ons"></div>' +
                '<button type="button" class="yt-base-landing__upsell-arrow yt-base-landing__upsell-arrow--next" data-yt-blend-upsell-next aria-label="More add-ons">&#8250;</button>' +
              '</div>';
            list.parentNode.insertBefore(carouselEl, list.nextSibling);
            var grid = carouselEl.querySelector('[data-yt-blend-upsell-grid]');
            carouselEl.querySelector('[data-yt-blend-upsell-prev]').addEventListener('click', function () {
              var card = grid.querySelector('.yt-base-landing__card');
              grid.scrollBy({ left: -((card ? card.getBoundingClientRect().width : 200) + 12) * 1.5, behavior: 'smooth' });
            });
            carouselEl.querySelector('[data-yt-blend-upsell-next]').addEventListener('click', function () {
              var card = grid.querySelector('.yt-base-landing__card');
              grid.scrollBy({ left: ((card ? card.getBoundingClientRect().width : 200) + 12) * 1.5, behavior: 'smooth' });
            });
            renderEditableRows();
            renderUpsellCards();
            // First paint keeps the server-priced offers; only the per-line
            // prices and totals row are painted here (no re-pricing).
            paintLinePrices();
          })
          .catch(function () { /* editing is an enhancement — page stays read-only */ });
      })();
    }

    // ── Nutritional Information accordion (leaflet-exact, founder spec:
    // panel name | typical per daily dose | %NRV — combined base + add-ons,
    // served pre-ordered by /formula/:id/pdp) ─────────────────────────
    if (list && payload.nutrition && payload.nutrition.length) {
      var nutriRows = payload.nutrition.map(function (r) {
        return '<tr>' +
          '<td>' + esc(r.panel_name) + '</td>' +
          '<td class="yt-blend-nutri__num">' + esc(r.dose_display || '') + '</td>' +
          '<td class="yt-blend-nutri__num">' + (r.nrv_pct != null ? esc(String(r.nrv_pct)) + '%' : '&ndash;') + '</td>' +
        '</tr>';
      }).join('');
      var acc = document.createElement('div');
      acc.className = 'yt-blend-nutri';
      acc.innerHTML =
        '<style>' +
          '.yt-blend-nutri{margin:16px 0 4px;font-family:var(--yt-font-body,Quicksand,sans-serif);}' +
          '.yt-blend-nutri__toggle{width:100%;display:flex;justify-content:space-between;align-items:center;background:none;border:1.5px solid #3c3c3b;border-radius:10px;padding:13px 16px;font-family:var(--yt-font-heading,Oswald,sans-serif);font-weight:500;font-size:13px;letter-spacing:.08em;text-transform:uppercase;cursor:pointer;color:#3c3c3b;}' +
          '.yt-blend-nutri__toggle:after{content:"+";font-size:18px;line-height:1;}' +
          '.yt-blend-nutri.is-open .yt-blend-nutri__toggle:after{content:"\\2212";}' +
          '.yt-blend-nutri__panel{display:none;overflow-x:auto;}' +
          '.yt-blend-nutri.is-open .yt-blend-nutri__panel{display:block;}' +
          '.yt-blend-nutri table{width:100%;border-collapse:collapse;margin-top:10px;font-size:13px;}' +
          '.yt-blend-nutri th,.yt-blend-nutri td{border:1px solid #3c3c3b;padding:6px 10px;text-align:left;}' +
          '.yt-blend-nutri th:nth-child(2),.yt-blend-nutri td:nth-child(2){width:130px;}' +
          '.yt-blend-nutri th:nth-child(3),.yt-blend-nutri td:nth-child(3){width:78px;}' +
          '.yt-blend-nutri th{font-size:11px;text-transform:uppercase;letter-spacing:.05em;vertical-align:bottom;}' +
          '.yt-blend-nutri__num{text-align:right !important;white-space:nowrap;}' +
        '</style>' +
        '<button type="button" class="yt-blend-nutri__toggle" aria-expanded="false">See full nutritional information</button>' +
        '<div class="yt-blend-nutri__panel">' +
          '<table>' +
            '<thead><tr><th>Nutritional information</th><th class="yt-blend-nutri__num">Typical per daily dose</th><th class="yt-blend-nutri__num">% NRV</th></tr></thead>' +
            '<tbody>' + nutriRows + '</tbody>' +
          '</table>' +
        '</div>';
      var listWrap = list.closest('section, div') || list.parentElement;
      if (listWrap && listWrap.parentElement) listWrap.parentElement.insertBefore(acc, listWrap.nextSibling);
      var nutriToggle = acc.querySelector('.yt-blend-nutri__toggle');
      nutriToggle.addEventListener('click', function () {
        var open = acc.classList.toggle('is-open');
        nutriToggle.setAttribute('aria-expanded', String(open));
      });
    }

    // ── stack total row (founder 2026-09-03) ───────────────────────────
    // Under the ingredient list: "N products · £X/mo · £Y a day" — the
    // blend read as a shelf of reasonably priced things, not one big price.
    function renderStackTotal(count, total, isOne) {
      if (!list) return;
      var n = Number(count) + (baseBlock ? 1 : 0);
      var row = list.querySelector('.yt-formula-pdp__stack-total');
      if (!row) {
        row = document.createElement('li');
        row.className = 'yt-formula-pdp__stack-total';
        list.appendChild(row);
      } else if (row !== list.lastElementChild) {
        list.appendChild(row);
      }
      var perDay = Math.round((Number(total) / 30) * 100) / 100;
      row.innerHTML =
        '<span class="yt-formula-pdp__stack-total-count">' + n + ' product' + (n === 1 ? '' : 's') + ', one scoop</span>' +
        '<span class="yt-formula-pdp__stack-total-figures">' +
          '<span class="yt-formula-pdp__stack-total-price">' + esc(money(Number(total))) + (isOne ? '' : '/mo') + '</span>' +
          '<span class="yt-formula-pdp__stack-total-day">' + esc(money(perDay)) + ' a day</span>' +
        '</span>';
    }

    // ── selection + breakdown + sticky ────────────────────────────────
    // Legacy default: 90-Day (best value) when available.
    var selected = payload.mode === 'onetime' ? 'onetime' : '1mo'; // 30-day default (founder 2026-09-01)
    var offers = Array.from(root.querySelectorAll('[data-yt-plan]'));
    var stickyTitle = root.querySelector('[data-yt-sticky-title]');
    var stickySub = root.querySelector('[data-yt-sticky-sub]');
    var stickyPill = root.querySelector('[data-yt-sticky-pill]');
    var stickyMenu = root.querySelector('[data-yt-sticky-menu]');

    function renderBreakdown() {
      var bd = root.querySelector('[data-yt-breakdown]');
      if (!bd) return;
      var isOne = selected === 'onetime';
      var is90 = selected === '3mo';
      var total = isOne ? oneTimePrice : is90 ? monthly90 : subPrice;
      // Fee at the selected basis: £15 sub / £18.75 one-time / £13.13 90-day
      // monthly (18.75 × 0.7) — lockstep with the ladder.
      var feeShown = isOne
        ? Math.round(fee * 1.25 * 100) / 100
        : is90
          ? Math.round(fee * 1.25 * 0.7 * 100) / 100
          : fee;
      var feeEl = bd.querySelector('[data-yt-blend-fee]');
      var totalEl = bd.querySelector('[data-yt-breakdown-total]');
      if (feeEl) feeEl.textContent = money(feeShown);
      // B5: no fee line on base blends — hide the whole row; the base's own
      // per-basis price fills the top row instead.
      if (feeEl && fee === 0) {
        var feeLine = feeEl.closest('li, .yt-formula-pdp__breakdown-row, div');
        if (feeLine) feeLine.style.display = 'none';
      }
      var basePart = 0;
      if (baseBlock) {
        basePart = isOne ? Number(baseBlock.prices.one_time)
          : is90 ? Number(baseBlock.prices.monthly_90d)
          : Number(baseBlock.prices.sub);
        var bp = root.querySelector('[data-yt-base-price]');
        if (bp) bp.textContent = money(basePart);
      }
      if (totalEl) totalEl.innerHTML = esc(money(total)) +
        (isOne ? '' : '<span class="yt-formula-pdp__total-unit">/mo</span>');
      renderStackTotal(ings.length, total, isOne);
      // Per-ingredient £ shares — rescaled proportionally to the selected
      // offer's (price − fee) pool, exactly like the legacy PDP.
      var spans = Array.from(root.querySelectorAll('[data-yt-share]'));
      if (!spans.length) return;
      var base = spans.map(function (sp) { return Number(sp.getAttribute('data-yt-share')) || 0; });
      var sum = base.reduce(function (a, b) { return a + b; }, 0);
      var pool = Math.max(0, Math.round((total - feeShown - basePart) * 100) / 100);
      if (sum <= 0) return;
      var acc = 0;
      spans.forEach(function (sp, i) {
        var v = i === spans.length - 1
          ? Math.round((pool - acc) * 100) / 100
          : Math.round((base[i] / sum) * pool * 100) / 100;
        acc = Math.round((acc + v) * 100) / 100;
        sp.textContent = money(v);
      });
    }

    function renderSelection() {
      offers.forEach(function (label) {
        var match = label.getAttribute('data-yt-plan') === selected;
        label.classList.toggle('yt-formula-pdp__offer--selected', match);
        var radio = label.querySelector('input[type="radio"]');
        if (radio) radio.checked = match;
      });
      // Founder 2026-08-31: one line — the selected plan's price (the
      // plan-name line is gone to shorten the bar; the caret still opens
      // the plan menu, and the offer cards above name the plans).
      if (stickyTitle) {
        // Founder 2026-09-03: the per-day figure rides beside the price.
        var stickyTotal = selected === 'onetime' ? oneTimePrice : selected === '3mo' ? monthly90 : subPrice;
        stickyTitle.innerHTML = (selected === 'onetime' ? esc(money(oneTimePrice)) : esc(money(stickyTotal)) + '/mo') +
          '<span class="yt-formula-pdp__stickybar-day">' + esc(money(Math.round((stickyTotal / 30) * 100) / 100)) + ' a day</span>';
      }
      if (stickySub) stickySub.remove();
      renderBreakdown();
    }

    offers.forEach(function (label) {
      label.addEventListener('click', function () {
        selected = label.getAttribute('data-yt-plan') || '1mo';
        renderSelection();
      });
    });
    // Underlined "One Time Purchase £X" reveal — same one-shot treatment
    // as legacy: reveal the bubble (CSS keys off .is-revealed on the
    // offers wrap) and select it.
    var offersWrap = root.querySelector('[data-yt-offers]');
    var onetimeLink = root.querySelector('[data-yt-onetime-link]');
    if (onetimeLink && offersWrap) {
      onetimeLink.addEventListener('click', function () {
        offersWrap.classList.add('is-revealed');
        selected = 'onetime';
        renderSelection();
      });
    }
    if (selected === 'onetime' && offersWrap) offersWrap.classList.add('is-revealed');
    if (stickyPill && stickyMenu) {
      stickyPill.addEventListener('click', function () {
        var open = stickyMenu.hidden;
        stickyMenu.hidden = !open;
        stickyPill.setAttribute('aria-expanded', String(open));
      });
      stickyMenu.querySelectorAll('[data-yt-sticky-option]').forEach(function (opt) {
        opt.addEventListener('click', function () {
          selected = opt.getAttribute('data-yt-sticky-option') || '1mo';
          stickyMenu.hidden = true;
          stickyPill.setAttribute('aria-expanded', 'false');
          renderSelection();
        });
      });
    }

    // ── CTAs: add the component lines (+ free spoon, one per cart) ────
    var errorEl = root.querySelector('[data-yt-formula-error]');
    var busy = false;
    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    async function addAndGo(dest) {
      if (busy) return;
      busy = true;
      if (errorEl) errorEl.hidden = true;
      root.querySelectorAll('[data-yt-formula-addbasket], [data-yt-formula-checkout], [data-yt-sticky-addbasket]')
        .forEach(function (b) { b.disabled = true; });
      try {
        // Edited custom blend → fork a fresh formula first (same name) so
        // the cart lines match what's on screen.
        await ensureBlendCurrent();
        // Three purchase modes, three DISTINCT group keys (intake
        // partitions pots by key; the drawer groups cards by it):
        //   1mo    → the registered 30-day set + its rb key
        //   3mo    → the registered 90-day TWIN set + its own rb key
        //   onetime→ the 30-day set, plan-less, key + ':ot' (#1116-proven)
        var isOneTime = selected === 'onetime';
        var is90Add = selected === '3mo' && has90;
        // 9 Oct 2026: `_yt_formula` on every line — the basket drawer's "Add
        // to your scoop" reads the blend's recipe from it to re-mint.
        var props = Object.assign({}, payload.bundle.properties || {}, payload.formula_id ? { _yt_formula: payload.formula_id } : {});
        if (is90Add) {
          props = Object.assign({}, props, { _rc_bundle: payload.bundle.bundle_key_90d });
        } else if (isOneTime && props._rc_bundle) {
          props = Object.assign({}, props, { _rc_bundle: props._rc_bundle + ':ot' });
        }
        var sourceItems = is90Add ? payload.bundle.items_90d : payload.bundle.items;
        // 9 Oct 2026: the free spoon is first-order only — a signed-in repeat customer does not get it in the cart.
        if (window.__ytRepeatCustomer === true && window.__ytSpoonVariantId) { sourceItems = sourceItems.filter(function (i) { return Number(i.variant_id) !== Number(window.__ytSpoonVariantId); }); }
        var items = sourceItems.map(function (i) {
          var line = {
            id: Number(i.variant_id),
            quantity: i.quantity || 1,
            properties: props,
          };
          if (is90Add && i.selling_plan_90d) line.selling_plan = Number(i.selling_plan_90d);
          else if (!is90Add && !isOneTime && i.selling_plan_30d) line.selling_plan = Number(i.selling_plan_30d);
          return line;
        });
        // (The free weighing spoon arrives as one of payload.bundle.items —
        // the formulator includes it in the registered bundle, so it groups
        // under the tin like the fee. No separate push needed.)
        // Shopify PREPENDS new lines — submit reversed so the cart displays
        // in payload order: base first, add-ons, then fee/spoon (founder
        // 2026-08-27).
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ items: items.slice().reverse() }),
        });
        if (!res.ok) {
          var detail = 'Something went wrong adding your blend — please try again.';
          try {
            var err = await res.json();
            if (err && (err.description || err.message)) detail = err.description || err.message;
          } catch (_e) {}
          throw new Error(detail);
        }
        if (dest === '/cart' && window.__ytCartDrawer) {
          // Side cart, not the full page (founder 2026-08-26).
          await window.__ytCartDrawer.open();
          busy = false;
          root.querySelectorAll('[data-yt-formula-addbasket], [data-yt-formula-checkout], [data-yt-sticky-addbasket]')
            .forEach(function (b) { b.disabled = false; });
          return;
        }
        window.location.assign(dest);
      } catch (e) {
        showError(e && e.message ? e.message : 'Something went wrong — please try again.');
        busy = false;
        root.querySelectorAll('[data-yt-formula-addbasket], [data-yt-formula-checkout], [data-yt-sticky-addbasket]')
          .forEach(function (b) { b.disabled = false; });
      }
    }

    root.querySelectorAll('[data-yt-formula-addbasket], [data-yt-sticky-addbasket]').forEach(function (b) {
      b.addEventListener('click', function () { addAndGo('/cart'); });
    });
    root.querySelectorAll('[data-yt-formula-checkout]').forEach(function (b) {
      b.addEventListener('click', function () { addAndGo('/checkout'); });
    });

    renderSelection();
    root.removeAttribute('data-yt-hydrating');
    if (window.ytTrack) { try { ytTrack('blend_pdp_viewed', { has_base: !!baseBlock }); } catch (_e) {} }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
